import React, { ReactElement } from 'react';
import moment from 'moment';
import classnames from 'classnames/bind';
import _ from 'lodash';
import { DAILY_BUDGET_STATE } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import { AdType, DailyBudgetPlan } from 'core/rtbCampaign/RtbCampaign';
import styles from './rtbCampaignInfo.module.scss';
import i18n from 'i18n';
import { AlertDialog } from 'components/AlertDialog';
import { Trans } from 'react-i18next';
import { FormikField } from 'components/form/field/FormikField';
import { LabelField } from 'components/form/field/LabelField';
import { CustomField } from 'components/form/field/CustomField';
import { RadioField } from 'components/form/field/RadioField';
import warningIcon from 'assets/warning.svg';
import { DefaultRtbCampaignManager, RtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { BidEffectivenessCard } from './BidEffectivenessCard';
import { toBidCap } from 'core/pchome/KeywordBidCompetitiveness';

const cx = classnames.bind(styles);

export type CampaignInfoState = {
  alertContent?: string | ReactElement | ReactElement[],
  needShowDailyBudgetAlert: boolean
};
class RtbCampaignInfo extends React.Component<any, CampaignInfoState> {

  turnOnNeedShowDailyBudgetAlert: () => void;
  rtbCampaignManager: RtbCampaignManager;

  budgetTypeOptions = [{
    label: i18n.t<string>('campaignInfo.labels.schedule'),
    value: DailyBudgetPlan.SCHEDULE,
    extra: i18n.t<string>('campaignInfo.labels.cannotModifyHint')
  }, {
    label: i18n.t<string>('campaignInfo.labels.daily'),
    value: DailyBudgetPlan.DAILY,
    extra: i18n.t<string>('campaignInfo.labels.cannotModifyHint')
  }];

  constructor (props) {
    super(props);
    this.state = {
      alertContent: undefined,
      needShowDailyBudgetAlert: false
    };
    this.turnOnNeedShowDailyBudgetAlert = _.partial(this.setNeedShowDailyBudgetAlert, true);
    this.rtbCampaignManager = new DefaultRtbCampaignManager();
  }

  getDailyBudgetTips () {
    const { dailyBudgetState } = this.props;
    switch (dailyBudgetState) {
      case DAILY_BUDGET_STATE.OVER_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.overDailyBudget'>
            ...<span className='text-danger'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.UNDER_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.underDailyBudget'>
            ...<span className='text-danger'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.MEET_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.meetDailyBudget'>
            ...<span className='text-dark'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.DEFAULT:
      default:
        return;
    }
  }

  setNeedShowDailyBudgetAlert = (needShow) => {
    this.setState({
      needShowDailyBudgetAlert: needShow
    });
  }

  checkIsUnderBudget = () => {
    if (this.state.needShowDailyBudgetAlert && this.props.dailyBudgetState === DAILY_BUDGET_STATE.UNDER_BUDGET) {
      this.setState({
        alertContent: (
          <Trans i18nKey='campaign.descriptions.underDailyBudget'>
            ...<span className='text-danger'>...</span>...
          </Trans>
        )
      });
    }
  }

  dismissAlertWindow = () => {
    this.setState({
      alertContent: undefined,
      needShowDailyBudgetAlert: false
    });
  }

  renderTargetBudgetInput = () => {
    const {
      currency,
      showBudgetHint,
      remainBudget
    } = this.props;

    let hint: ReactElement | undefined;
    if (showBudgetHint) {
      hint = (
        <Trans i18nKey='campaign.descriptions.remainBudget'>
          Remain budget <span className='text-dark'>{{ currency }}{{ remainBudget }}</span>
        </Trans>
      );
    }

    const inputProps = {
      label: i18n.t<string>('campaignInfo.labels.totalBudget'),
      prefix: currency,
      name: 'budget',
      type: 'number',
      min: 0,
      autoComplete: 'off',
      onBlur: this.checkIsUnderBudget,
      onChange: this.turnOnNeedShowDailyBudgetAlert,
      hint,
      fieldContentWidth: 424,
      postComponent: this.renderCampaignAnalyticsHint('lifetimeBudgetRank')
    };

    return (
      <FormikField.InputGroup
        {...inputProps}
      />
    );
  }

  renderDailyBudgetPlan () {
    const {
      currency,
      dailyBudget
    } = this.props;
    const isScheduleDistribution = _.isNil(dailyBudget) || dailyBudget === 0;
    const postComponent = this.renderCampaignAnalyticsHint('dailyBudgetRank');
    return (
      <CustomField
        label={i18n.t<string>('campaignInfo.labels.dailyBudgetPlan')}
        name='dailyBudgetPlan'
      >
        {/*
          這一列的欄位是包在外層 CustomField 裡的第二層 withFieldLayout，而它們沒有自己的 label，
          所以那一層只會渲染放內容的 Col、不會有配對的標籤 Col。inputColSm 若留預設的 9，內容就
          只拿到 75% 的寬度，同一頁其他列的欄位與提示都會比它右邊 130 到 170 個 pixel（隨視窗寬度
          變動）——實際上是白白被吃掉的縮排。給 12 讓它佔滿，這一列才和其他列同寬、提示也才對齊。
        */}
        {
          isScheduleDistribution ?
          <LabelField
            value={i18n.t<string>('campaignSummary.labels.budgetScheduleDistribution')}
            formGroupClassName={styles.marginBottom0}
            inputColSm={12}
            postComponent={postComponent}
          /> :
          <>
            <LabelField
              value={i18n.t<string>('campaignInfo.labels.daily')}
              formGroupClassName={styles.marginBottom7}
              inputColSm={12}
            />
            <FormikField.InputGroup
              prefix={currency}
              name='dailyTargetBudget'
              hint={this.getDailyBudgetTips()}
              type='number'
              min={0}
              autoComplete='off'
              onBlur={this.checkIsUnderBudget}
              onChange={this.turnOnNeedShowDailyBudgetAlert}
              formGroupClassName={styles.marginBottom0}
              inputColSm={12}
              postComponent={postComponent}
            />
          </>
        }
      </CustomField>
    );
  }

  renderCampaignScheduling () {
    const { startDate, endDate } = this.props;
    const endDateMoment = moment(endDate);
    const startDateMoment = moment(startDate);
    const days = endDateMoment.diff(startDateMoment, 'days') + 1;
    const hours = endDateMoment.diff(startDateMoment, 'hours') + 1;
    if (days > 1) {
      return (
        <Trans i18nKey='campaign.descriptions.campaignSchedulingDay'>
          Total <span className='text-dark'>{{ days }} days</span>
        </Trans>
      );
    } else if (days === 1) {
      return (
        <Trans i18nKey='campaign.descriptions.campaignSchedulingHour'>
          Total <span className='text-dark'>{{ hours }} hours</span>
        </Trans>
      );
    }
  }

  renderCampaignAnalyticsHint (fieldName: string) {
    const { campaignAnalytics } = this.props;
    const hint = this.rtbCampaignManager.getCampaignAnalyticsHint(campaignAnalytics, fieldName);
    if (!hint) {
      return undefined;
    }
    return (
      <div className={styles.campaignAnalyticsHint}>
        <img
          src={warningIcon}
          alt='warning'
        />
        {hint}
      </div>
    );
  }

  render () {
    const {
      orderName,
      minDate,
      maxDate,
      canEditPriceModel,
      priceModelOptions,
      currency,
      canUseDailyBudget,
      canEditBudgetPlan,
      onChangeDailyBudgetPlan: handleChangeDailyBudgetPlan,
      onChangePriceModel: handleChangePriceModel,
      showTagsPlaceholder,
      isCampaignStart,
      dailyBudgetType,
      showDeliverType,
      showTags,
      bidPriceRange,
      adType,
      selectedKeywordRows,
      bidPrice
    } = this.props;

    // 只有關鍵字型活動才有「出價效益參考」卡片。其他 adType 的 `基本資訊`
    // 維持原本的純單欄 form —— 這張卡是唯一把它變成兩欄的理由。
    const showBidEffectiveness = adType === AdType.KEYWORD;
    // formik 的 `bidPrice` 來自 <input type='number'>，清空時是 ''、輸入中可能是字串。
    // 走與「出價試算」面板同一支 toBidCap —— 判定共用了但輸入的正規化沒共用，等於沒共用
    // （出價 0 曾經在面板是「未試算」、在這裡卻變成「買不到量」）。
    const bidCap = toBidCap(bidPrice);

    const bidPriceHint = bidPriceRange.recommend ?
      <Trans i18nKey={'campaign.descriptions.rsCpcPriceMinimum'}>
        The price have to over <span className='text-dark'>{{ min: currency + bidPriceRange.min }}</span>, recommend {{ rmin: currency + bidPriceRange.recommend.min }}~{{ rmax: currency + bidPriceRange.recommend.max }}
      </Trans> :
      bidPriceRange.min && bidPriceRange.min > 0 ?
      <Trans i18nKey={'campaign.descriptions.orderPriceTips'}>
        The price have to over <span className='text-dark'>{{ min: currency + bidPriceRange.min }}</span>
      </Trans>
      : '';
    const priceModelClassName = cx('priceModel', {
      'componentWithTips': true
    });
    const dateRangePickerClassName = cx('componentWithTips', {
      disableStart: isCampaignStart
    });
    const onChangeSchedule = () => {
      this.setNeedShowDailyBudgetAlert(true);
    };
    const priceModelFormatter = priceModel => i18n.t<string>(`campaign.labels.${priceModel}`);
    const mainColumn = (
      <div className={styles.component}>
        {this.state.alertContent &&
          <AlertDialog
            title={i18n.t<string>('common.warning')}
            message={this.state.alertContent}
            danger={false}
            dismiss={this.dismissAlertWindow}
          />
        }
        <LabelField
          label={i18n.t<string>('campaignInfo.labels.order')}
          name='orderName'
          value={orderName}
        />
        <FormikField.Input
          label={i18n.t<string>('campaignInfo.labels.campaignName')}
          name='name'
        />
        <FormikField.DateRangePicker
          label={i18n.t<string>('campaignInfo.labels.dayRange')}
          className={dateRangePickerClassName}
          minDate={minDate}
          maxDate={maxDate}
          startDateFormikName='startDate'
          endDateFormikName='endDate'
          name='dayRange'
          timeInterval={60}
          startDatePickerDisabled={isCampaignStart}
          onPopOverClosed={this.checkIsUnderBudget}
          onChange={onChangeSchedule}
          hoverHint={i18n.t<string>('campaignInfo.labels.dateCannotModifyHint')}
          hint={this.renderCampaignScheduling()}
          fieldContentWidth={424}
        />
        {
          canEditPriceModel ?
          <FormikField.Select
            className={priceModelClassName}
            name='priceModel'
            label={i18n.t<string>('campaignInfo.labels.priceModel')}
            simpleValue
            options={priceModelOptions}
            onChange={handleChangePriceModel}
            hoverHint={i18n.t<string>('campaignInfo.labels.cannotModifyPriceModelHint')}
          /> :
          <FormikField.Label
            name='priceModel'
            label={i18n.t<string>('campaignInfo.labels.priceModel')}
            formatter={priceModelFormatter}
          />
        }
        <FormikField.InputGroup
          label={i18n.t<string>('l1Object.labels.bidStrategy.lowest_cost_with_bid_cap')}
          name='bidPrice'
          prefix={currency}
          type='number'
          min={0}
          className={styles.bidPrice}
          hint={bidPriceHint}
          postComponent={this.renderCampaignAnalyticsHint('bidPrice')}
        />
        {this.renderTargetBudgetInput()}
        {canUseDailyBudget && !canEditBudgetPlan &&
          this.renderDailyBudgetPlan()}
        {canUseDailyBudget && canEditBudgetPlan && (
          <RadioField
            name='dailyBudgetType'
            label={i18n.t<string>('campaignInfo.labels.dailyBudgetPlan')}
            className={styles.radio}
            useOptionExtraAsHoverTip={true}
            options={this.budgetTypeOptions}
            value={dailyBudgetType}
            onChange={handleChangeDailyBudgetPlan}
            fieldContentWidth={424}
            postComponent={(+dailyBudgetType === DailyBudgetPlan.SCHEDULE)
              ? this.renderCampaignAnalyticsHint('dailyBudgetRank')
              : undefined
            }
          />
        )}
        {+dailyBudgetType === DailyBudgetPlan.DAILY && canEditBudgetPlan && (
          <FormikField.InputGroup
            label=''
            formGroupClassName={styles.dailyBudgetInput}
            name='dailyTargetBudget'
            prefix={currency}
            hint={this.getDailyBudgetTips()}
            type='number'
            min={0}
            autoComplete='off'
            onBlur={this.checkIsUnderBudget}
            onChange={this.turnOnNeedShowDailyBudgetAlert}
            postComponent={this.renderCampaignAnalyticsHint('dailyBudgetRank')}
          />
        )}
        {showDeliverType &&
          <FormikField.ToggleButton
            name='deliverType'
            label={i18n.t<string>('campaign.labels.deliverType')}
            options={this.props.campaignDeliverTypeOptions}
          />
        }
        {showTags &&
          <FormikField.Tags
            label={i18n.t<string>('campaign.labels.tags')}
            name='tags'
            placeholder={showTagsPlaceholder ? i18n.t<string>('campaign.placeholders.tags') : ''}
          />
        }
      </div>
    );

    return (
      <fieldset>
        <legend>
          <span>{i18n.t<string>('campaignInfo.labels.title')}</span>
        </legend>
        {showBidEffectiveness ? (
          <div className={styles.bidEffectivenessLayout}>
            {mainColumn}
            {/* 這層 aside 是為了讓卡片能絕對定位 —— 卡片因此對版面高度零貢獻，
                左欄（廣告基本資訊與計價模式）的高度永遠只由表單自己決定。
                理由與量測見 rtbCampaignInfo.module.scss。 */}
            <div className={styles.bidEffectivenessAside}>
              <BidEffectivenessCard
                selectedKeywordRows={selectedKeywordRows}
                bidCap={bidCap}
                bidFloor={bidPriceRange.min}
              />
            </div>
          </div>
        ) : mainColumn}
      </fieldset>
    );
  }
}

export default RtbCampaignInfo;
